/**
 * Read-Event Audit Log — beta-local, in-memory sink for "who viewed what."
 *
 * Companion to the existing write-side Audit Log. Read tracking is required
 * for SOC-2 conversations once a real customer wires up; capturing it now
 * (even in memory) means the surfaces that NEED to log reads are already
 * instrumented when Cloud lands and only the sink swaps to Supabase.
 *
 * What gets logged:
 *   • viewer (role + display name when available)
 *   • surface (route key, e.g. "ar-collections")
 *   • entityRef when the view is scoped to one record (jobId, customerId…)
 *   • sensitivity tag — "margin" / "payout" / "contractValue" / "default"
 *
 * Frontend-only; resets on hard reload. Capped at 1,000 entries (ring buffer).
 */

export type ReadSensitivity = "margin" | "payout" | "contractValue" | "default";

export interface ReadEvent {
  id: string;
  at: string;                 // ISO timestamp
  viewerRole?: string;
  viewerName?: string;
  surface: string;            // e.g. "trial-balance" or "job-one-pager"
  entityRef?: { kind: string; id: string };
  sensitivity: ReadSensitivity;
}

const RING_CAP = 1000;
const events: ReadEvent[] = [];
const listeners = new Set<() => void>();

let counter = 0;

export function logReadEvent(input: Omit<ReadEvent, "id" | "at"> & { at?: string }): ReadEvent {
  counter += 1;
  const ev: ReadEvent = {
    id: `re_${Date.now()}_${counter}`,
    at: input.at ?? new Date().toISOString(),
    viewerRole: input.viewerRole,
    viewerName: input.viewerName,
    surface: input.surface,
    entityRef: input.entityRef,
    sensitivity: input.sensitivity,
  };
  events.unshift(ev);
  if (events.length > RING_CAP) events.length = RING_CAP;
  listeners.forEach(l => l());
  return ev;
}

export function getReadEvents(): ReadEvent[] {
  return events.slice();
}

export function subscribeReadEvents(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
